import {
  Box,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
  SelectItem,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { Template } from 'lib-shared-legacy-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { FormErrors, InjectedFormProps, reduxForm } from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { getRequestID } from 'components/shared/util/request';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { withTemplates, WithTemplatesInjectedProps } from 'hocs';

import type {
  FlowEmailNodeReplyPayloadT,
  FlowEmailNodeReplyT,
} from '../nodeTypes';

import styles from './reply.module.scss';

type StateProps = ReturnType<typeof mapStateToProps>;

type FormValues = {
  auto_reply_template_id: string;
};

type ReplyModalReduxFormProps = InjectedFormProps<FormValues, Props>;

type OwnProps = FlowManagerEditModalComponentT<
  FlowEmailNodeReplyT,
  Record<string, never>
>;

type Props = OwnProps &
  StateProps &
  WithTemplatesInjectedProps &
  ReplyModalReduxFormProps;

type Validation = {
  auto_reply_template_id?: string;
};

const validate = (values: FormValues): FormErrors => {
  const errors: Validation = {};

  if (!values) {
    return errors;
  }

  if (!values.auto_reply_template_id) {
    errors.auto_reply_template_id = 'Required - please select a template';
  }

  return errors;
};

type StateT = {
  payload?: FlowEmailNodeReplyPayloadT;
  selectedTemplate?: Template;
  currentTemplateId?: string;
};

class ReplyModalImpl extends Component<Props, StateT> {
  constructor(props: Props) {
    super(props);

    const { node } = props;
    this.state = {
      payload:
        node.nodeType === FlowNodeTypes.AUTOREPLY ? node.payload : undefined,
      currentTemplateId: node.payload?.auto_reply_template_id,
    };
  }

  async componentDidMount() {
    const { templates } = this.props;
    templates.getItems();
    if (this.state.payload?.auto_reply_template_id) {
      const currentTemplate = await templates.readTemplateById(
        this.state.payload.auto_reply_template_id,
      );
      this.setState({ selectedTemplate: currentTemplate?.data });
    }
  }

  @boundMethod
  handleSaveClick(values: FormValues) {
    const { node, replaceNode, triggerClose } = this.props;

    const payload: FlowEmailNodeReplyPayloadT = {
      auto_reply_template_id:
        this.state.payload?.auto_reply_template_id ??
        values.auto_reply_template_id,
      next_node_id: node.payload?.next_node_id || getRequestID(),
    };

    const hasReplyID = typeof values.auto_reply_template_id === 'string';
    const nodeText = `Reply ${
      hasReplyID ? `(${values.auto_reply_template_id})` : ''
    }`;

    const nodeOptions: FlowEmailNodeReplyT = {
      ...node,
      badges: [],
      childIds: [],
      icon: 'i-forward',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.AUTOREPLY,
      overlay: [],
      payload,
      text: nodeText,
    };

    replaceNode(nodeOptions);
    triggerClose();
  }

  getPagination() {
    const { templates } = this.props;
    return {
      nextEnable: templates.hasNextPage,
      onNext: templates.nextPage,
      previousEnable: templates.hasPreviousPage,
      onPrevious: templates.previousPage,
    };
  }

  getTemplateOptions() {
    return this.props.templates.items;
  }

  @boundMethod
  onTemplateSearch(searchTerm: string | undefined) {
    const { templates } = this.props;
    if (searchTerm !== undefined && searchTerm !== templates.params.search) {
      templates.paramsChange({ search: searchTerm });
    }
  }

  @boundMethod
  onTemplateSelect(template: Template) {
    const payload = {
      auto_reply_template_id: template.id,
      next_node_id: this.state.payload?.next_node_id,
    };
    this.setState({
      payload,
      selectedTemplate: template,
    });
  }

  @boundMethod
  render() {
    const {
      handleSubmit,
      node,
      nodeLabel,
      templates: { getItemsAsyncStatus },
      triggerClose,
    } = this.props;

    const isLoading = getItemsAsyncStatus === 'loading';

    const isChanged =
      this.state.selectedTemplate?.id != this.state.currentTemplateId;

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={triggerClose}>
          <div className={styles.description}>
            Send an automatic reply based on a custom template.
            <br />
            Auto-reply templates can be created under{' '}
            <strong>Settings &gt; Templates</strong>.
          </div>
        </ModalHead>
        <ModalBody>
          <Select
            className={styles.dropdownContainer}
            itemComponent={SelectItem}
            items={this.getTemplateOptions()}
            pagination={this.getPagination()}
            onSearch={this.onTemplateSearch}
            onSelect={this.onTemplateSelect}
            selectedItems={
              this.state.selectedTemplate ? [this.state.selectedTemplate] : []
            }
          ></Select>
        </ModalBody>
        <ModalFoot
          confirmOnClick={handleSubmit(this.handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{
            disabled: isLoading || !isChanged,
          }}
          cancelOnClick={triggerClose}
        />
      </Box>
    );
  }
}

const mapStateToProps = (
  _state: RootReducerStateT,
  { node: { payload } }: OwnProps,
) => {
  return {
    initialValues: payload || {
      auto_reply_template_id: '',
    },
  };
};

export const ReplyModal = connect<
  StateProps,
  Record<string, never>,
  OwnProps,
  RootReducerStateT
>(mapStateToProps)(
  reduxForm<FormValues, Props>({
    form: 'emailFlowReplyForm', // a unique identifier for this form
    enableReinitialize: true,
    validate,
  })(
    withTemplates<Props>(ReplyModalImpl, {
      initialParams: { type: 'email_auto_reply' },
    }),
  ),
);
